import React from 'react';
import { Droppable } from '@hello-pangea/dnd';

// Module Components
import PreviewBlock from './PreviewBlock';
// Region caption hidden (2026-09-28) — see the commented-out <RegionHeader> below.
// import RegionHeader from './RegionHeader';

// Constants
import {
  CARD_BLOCK_DND_TYPE,
  infoGapDroppableId,
  infoRowDroppableId,
} from '../../../constants';

// Types
import { FieldId, FieldConfiguration, InfoRow, RegionId } from '../../../types';

interface InfoRegionProps {
  /** Read-only — shared with the document index (state/cardIndex). */
  rows: readonly InfoRow[];
  isDragging: boolean;
  activeRegion: RegionId | null;
  selectedFieldId: FieldId | null;
  configurationFor: (fieldId: FieldId) => FieldConfiguration;
  /** Card-wide: price rows carry the unit of measure (spec R69, R72). */
  onSelectBlock: (fieldId: FieldId) => void;
  onDeleteBlock: (fieldId: FieldId) => void;
}

/**
 * A thin permanent lane between rows. Dropping here inserts the block as its own
 * row at that position (spec R50), and the lane fills with an insert bar while a
 * drag is over it (spec R49).
 *
 * The lane keeps its height whether or not a drag is in progress: growing drop
 * targets only during a drag would move every row *after* the library has
 * measured them, which misaligns the drop.
 */
const InfoGapLane = ({ index }: { index: number }) => (
  <Droppable droppableId={infoGapDroppableId(index)} type={CARD_BLOCK_DND_TYPE}>
    {(provided, snapshot) => (
      <div
        ref={provided.innerRef}
        {...provided.droppableProps}
        className="tw-min-h-[8px] tw-flex tw-items-center"
      >
        <span
          className={`tw-w-full tw-h-0.5 tw-rounded-full ${
            snapshot.isDraggingOver ? 'tw-bg-accent-bold' : 'tw-bg-transparent'
          }`}
        />
        {provided.placeholder}
      </div>
    )}
  </Droppable>
);

const justifyClasses: Record<FieldConfiguration['alignment'], string> = {
  left: 'tw-justify-start',
  center: 'tw-justify-center',
  right: 'tw-justify-end',
};

/**
 * The Info region: the card's main vertical stack (spec R48).
 *
 * Each rendered row is its own horizontal drop target, which is how "drop on the
 * left or right side of an existing block" becomes a real, keyboard-reachable
 * placement rather than pointer geometry: dropping at index 0 lands left of the
 * row's first block, any later index lands to the right of its neighbour
 * (spec R51).
 */
const InfoRegion = ({
  rows,
  // `isDragging` and `activeRegion` only fed the region caption, hidden 2026-09-28; the
  // props stay on the interface so CardPreview is unchanged. Restore with the caption.
  // isDragging,
  // activeRegion,
  selectedFieldId,
  configurationFor,
  onSelectBlock,
  onDeleteBlock,
}: InfoRegionProps) => {
  /**
   * A row holding a single block follows that block's own alignment, so choosing
   * Right actually shifts a short block instead of only re-aligning the text
   * inside it (spec R44). A paired row keeps its blocks packed together.
   */
  const rowJustifyClass = (row: InfoRow): string =>
    row.placements.length === 1
      ? justifyClasses[configurationFor(row.placements[0].fieldId).alignment]
      : justifyClasses.left;

  return (
  <div className="tw-flex-1 tw-min-w-0">
    {/* Region caption hidden on request (2026-09-28): the preview no longer labels this region.
        Restore by uncommenting this and the RegionHeader import above.
    <RegionHeader region="info" isDragging={isDragging} isActive={activeRegion === 'info'} /> */}

    <div className="tw-px-3 tw-py-2">
      {rows.map((row, rowIndex) => (
        <React.Fragment key={row.rowKey}>
          <InfoGapLane index={rowIndex} />

          <Droppable
            droppableId={infoRowDroppableId(rowIndex)}
            direction="horizontal"
            type={CARD_BLOCK_DND_TYPE}
          >
            {(provided, snapshot) => (
              <div
                ref={provided.innerRef}
                {...provided.droppableProps}
                className={`tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-w-full tw-rounded-sm tw-min-h-[24px] ${rowJustifyClass(
                  row
                )} ${
                  snapshot.isDraggingOver
                    ? 'tw-outline tw-outline-2 tw-outline-accent tw-bg-accent-tint'
                    : ''
                }`}
              >
                {row.placements.map((placement, index) => (
                  <PreviewBlock
                    key={placement.fieldId}
                    placement={placement}
                    configuration={configurationFor(placement.fieldId)}
                    index={index}
                    isSelected={selectedFieldId === placement.fieldId}
                    onSelect={() => onSelectBlock(placement.fieldId)}
                    onDelete={() => onDeleteBlock(placement.fieldId)}
                  />
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </React.Fragment>
      ))}

      <InfoGapLane index={rows.length} />
    </div>
  </div>
  );
};

export default InfoRegion;
